import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// components
import {
  Avatar,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useSnackbar
} from '@growdash/design-system/atoms';
import {
  ConfirmDeleteDialog,
  DataTable,
  MultiSelect
} from '@growdash/design-system/molecules';

// utils
import { fNumber } from '@utils/format-number';
import { currencyString } from '@utils/helpers';
import {
  FetchCommissonResponse,
  ICommissonGroupByBrand
} from '@utils/interfaces';
// hooks
// store
import { RootState } from '@store/index';
// api
import { ManagementService } from '@modules/management/api';
import { EyeIcon, MoreHorizontal, PlusIcon } from 'lucide-react';
import { AddCommissionForm } from './AddCommissionForm';

export default function CommissionsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const currency = currencyString();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isAddCommissionOpen, setIsAddCommissionOpen] = useState(false);
  const { CHANNELS, BRANDS } = useSelector((state: RootState) => state.filter);
  const [selectedBrandIds, setSelectedBrandIds] = useState<
    number[] | undefined
  >(undefined);
  const [tableData, setTableData] = useState<FetchCommissonResponse | null>(
    null
  );
  const [tableLoading, setLoading] = useState<boolean>(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] =
    useState<ICommissonGroupByBrand | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const handleAddCommissionSubmit = async (
    commission: ICommissonGroupByBrand,
    channelId: number
  ) => {
    try {
      await ManagementService.CreateCommission({
        channel_id: channelId,
        brandId: selectedBrand?.id ?? 0,
        yearly_renewal_fee: commission.yearly_renewal_fee,
        monthly_subscription_fee: commission.monthly_subscription_fee,
        percentage_commission: commission.percentage_commission,
        flat_fee_per_order: commission.flat_fee_per_order
      });
      enqueueSnackbar('Commission added successfully!', { variant: 'success' });
      fetchCommissions(page + 1, rowsPerPage);
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      setIsAddCommissionOpen(false);
    }
  };

  const tableLabels = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Brand',
      align: 'center'
    },
    {
      id: 'action',
      header: 'Action',
      align: 'center',
      cell: ({ row }: any) => {
        const detail = row.original;
        return (
          <div className='flex gap-2'>
            <Dialog>
              {detail.value?.length === 0 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DialogTrigger disabled>
                        <EyeIcon className='h-[17px] text-[#71717a] cursor-pointer opacity-30' />
                      </DialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>No Data</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <DialogTrigger>
                  <EyeIcon className='h-[17px] text-[#71717a] cursor-pointer' />
                </DialogTrigger>
              )}
              <DialogContent className='min-w-[760px]'>
                <DialogHeader>
                  <DialogTitle>Detail Table</DialogTitle>
                </DialogHeader>
                <DataTable
                  columns={detailTableLabels}
                  data={detail.value || []}
                  isBorderActive
                />
              </DialogContent>
            </Dialog>
            <PlusIcon
              className='h-[17px] text-[#71717a] cursor-pointer'
              onClick={() => {
                setSelectedBrand(detail);
                setIsAddCommissionOpen(true);
              }}
            />
          </div>
        );
      }
    }
  ];

  const detailTableLabels = [
    {
      id: 'channel',
      accessorKey: 'channel',
      header: 'Channel',
      cell: ({ row }: any) => {
        const channel = row?.getValue('channel');
        return (
          <Avatar className='w-8 h-8'>
            <AvatarImage
              alt={channel}
              src={`/assets/channel_logo/${channel?.name.toLowerCase()}.png`}
            />
          </Avatar>
        );
      }
    },
    {
      id: 'yearly_renewal_fee',
      accessorKey: 'yearly_renewal_fee',
      header: `Yearly Subscription Fee (${currency})`,
      align: 'center',
      cell: ({ row }: any) => (
        <div>
          {row.getValue('yearly_renewal_fee')} {currency}
        </div>
      )
    },
    {
      id: 'monthly_subscription_fee',
      accessorKey: 'monthly_subscription_fee',
      header: `Monthly Subscription Fee (${currency})`,

      cell: ({ row }: any) => (
        <div>
          {row.getValue('monthly_subscription_fee')} {currency}
        </div>
      )
    },
    {
      id: 'percentage_commission',
      accessorKey: 'percentage_commission',
      header: 'Commission %',
      align: 'center',
      cell: ({ row }: any) => (
        <div>{fNumber(row.getValue('percentage_commission'))} %</div>
      )
    },
    {
      id: 'flat_fee_per_order',
      accessorKey: 'flat_fee_per_order',
      header: `Flat Fee Per Order (${currency})`,
      align: 'center',
      cell: ({ row }: any) => (
        <div>
          {fNumber(row.getValue('flat_fee_per_order'))} {currency}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'center',
      cell: ({ row }: any) => (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem
              onClick={() => {
                setSelectedCommission(row.original);
                setEditModalOpen(true);
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedCommission(row.original);
                setDeleteModalOpen(true);
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const fetchCommissions = useCallback(
    async (pageNumber: number = page + 1, take: number = rowsPerPage) => {
      try {
        setLoading(true);
        const commissionResponse = await ManagementService?.getAllCommission(
          pageNumber,
          take,
          selectedBrandIds
        );
        setTableData(commissionResponse);
      } catch (error) {
        enqueueSnackbar(error?.message, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, selectedBrandIds, enqueueSnackbar]
  );

  const onDeleteCommission = async (id: number) => {
    try {
      // const deleteResponse =
      await ManagementService?.DeleteCommission(id);
      fetchCommissions(page + 1, rowsPerPage);
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      // setLoading(false)
    }
  };

  useEffect(() => {
    fetchCommissions(page + 1, rowsPerPage);
  }, [selectedBrandIds, page, rowsPerPage, fetchCommissions]);

  const handleDeleteCommission = async (id: number) => {
    await onDeleteCommission(id);
    setDeleteModalOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchCommissions(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchCommissions(page + 1, newRowsPerPage);
  };

  return (
    <>
      <Dialog open={isEditModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Commission</DialogTitle>
          </DialogHeader>
          <Input
            name='yearly_renewal_fee'
            placeholder='Yearly Renewal Fee'
            value={selectedCommission?.yearly_renewal_fee || ''}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                yearly_renewal_fee: Number(e.target.value)
              });
            }}
          />
          <Input
            name='monthly_subscription_fee'
            placeholder='Monthly Subscription Fee'
            value={selectedCommission?.monthly_subscription_fee || ''}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                monthly_subscription_fee: Number(e.target.value)
              });
            }}
          />
          <Input
            name='percentage_commission'
            placeholder='Commission %'
            value={selectedCommission?.percentage_commission || ''}
            onChange={e => {
              if (!selectedCommission) return;
              const value = Number(e.target.value);
              // Validate commission percentage between 0 and 100
              if (value < 0 || value > 100) {
                enqueueSnackbar(
                  'Commission percentage must be between 0 and 100',
                  {
                    variant: 'error'
                  }
                );
                return;
              }
              setSelectedCommission({
                ...selectedCommission,
                percentage_commission: value
              });
            }}
          />
          <Input
            name='flat_fee_per_order'
            placeholder='Flat Fee Per Order'
            value={selectedCommission?.flat_fee_per_order || ''}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                flat_fee_per_order: Number(e.target.value)
              });
            }}
          />
          <div className='flex justify-end gap-2 mt-4'>
            <Button
              onClick={async () => {
                try {
                  if (!selectedCommission) return;
                  await ManagementService.UpdateCommission(
                    {
                      yearly_renewal_fee: selectedCommission.yearly_renewal_fee,
                      monthly_subscription_fee:
                        selectedCommission.monthly_subscription_fee,
                      percentage_commission:
                        selectedCommission.percentage_commission,
                      flat_fee_per_order: selectedCommission.flat_fee_per_order
                    },
                    selectedCommission.id
                  );
                  enqueueSnackbar('Commission updated successfully!', {
                    variant: 'success'
                  });
                  fetchCommissions(page + 1, rowsPerPage);
                } catch (error) {
                  enqueueSnackbar(error?.message, { variant: 'error' });
                } finally {
                  setEditModalOpen(false);
                }
              }}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className='mt-6'>
        <Card>
          <CardHeader>
            <div className='flex justify-between items-center'>
              <CardTitle>Brand Comissions</CardTitle>
              <MultiSelect
                placeholder='Select Brands'
                popoverAlign='end'
                onReset={() => setSelectedBrandIds([])}
                className='w-max'
                maxCount={2}
                defaultValue={selectedBrandIds ?? ([] as any)}
                options={BRANDS?.map((brand: any) => ({
                  label: brand.name,
                  value: brand.id
                }))}
                onValueChange={setSelectedBrandIds}
              />
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              isLoading={tableLoading}
              columns={tableLabels}
              data={tableData?.data || []}
              isPaginationActive
              isServerSide
              pagination={{
                pageSize: rowsPerPage,
                pageIndex: page,
                pageCount: Math.ceil((tableData?.total ?? 0) / rowsPerPage),
                onPageChange: handlePageChange,
                onRowsPerPageChange: handleRowsPerPageChange
              }}
            />
          </CardContent>
        </Card>
      </div>
      <ConfirmDeleteDialog
        open={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title='Delete Commission?'
        content='Are you sure you want to delete this commission? This action cannot be undone.'
        action={
          <Button
            onClick={() =>
              selectedCommission &&
              handleDeleteCommission(selectedCommission.id)
            }
          >
            Delete
          </Button>
        }
      />
      <Dialog open={isAddCommissionOpen} onOpenChange={setIsAddCommissionOpen}>
        <DialogContent>
          <AddCommissionForm
            CHANNELS={CHANNELS}
            enqueueSnackbar={enqueueSnackbar}
            onCancel={() => setIsAddCommissionOpen(false)}
            onSubmit={handleAddCommissionSubmit}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
